import { supportCardName, type CardScore } from '@archon3d/core';
import { useEffect, useState } from 'react';
import {
  STORY_PACE,
  chanceNow,
  storyHeadline,
  storyMoment,
  storyNarration,
  sweep,
  type CardStory,
} from '../game/cardStory';
import type { ClockView } from '../game/countdown';
import type { CardClashView, SessionSnapshot } from '../game/session';

interface RevealProps {
  snapshot: SessionSnapshot;
  clash: CardClashView;
  portraits: Record<string, string>;
  portraitOf: (card: string) => string;
  clock: ClockView | null;
  onContinue: () => void;
}

/**
 * The reveal of a Card clash, told (audit step 18b): each side's support is laid down under its
 * fighter and moves the chances bar between them; a needle runs along the bar and stops where the
 * draw fell; the result is named, and waits for Continue. The first press of Continue shows the
 * result at once.
 */
export function CardReveal({
  snapshot,
  clash,
  portraits,
  portraitOf,
  clock,
  onContinue,
}: RevealProps) {
  const story = clash.story;
  const moment = storyMoment(story);
  const scores = clash.scores ?? [];
  const order = story?.order ?? clash.participants.map((p) => p.pieceId);
  const headline = storyHeadline(clash.participants, scores);
  const narration = () => storyNarration(snapshot.ctx, clash.participants, scores, story);
  return (
    <>
      <div className="played-supports" aria-label="The supports played">
        {order.map((id, i) => {
          const score = scores.find((s) => s.pieceId === id);
          const down = i < moment.placed;
          return (
            <SupportPlayed
              key={id}
              score={score}
              down={down}
              name={score?.cardId ? supportCardName(snapshot.ctx, score.cardId) : null}
              picture={score?.cardId ? portraits[portraitOf(score.cardId)] : undefined}
            />
          );
        })}
      </div>
      <ChanceBar snapshot={snapshot} clash={clash} order={order} />
      <section className="card-reveal" aria-label="The clash" aria-live="polite">
        {moment.over ? (
          <div className="card-result" data-testid="card-result">
            <h2>{headline.title}</h2>
            <p>{headline.note}</p>
          </div>
        ) : (
          <p className="card-narration" data-testid="card-narration">
            {narration()}
          </p>
        )}
        {clash.hold === 'continue' ? (
          <div className="row card-continue">
            <button className="primary" autoFocus data-testid="card-continue" onClick={onContinue}>
              {moment.over ? 'Continue' : 'Skip'}
            </button>
            {clock && (
              <small data-testid="card-clock">or the game goes on in {secondsLeft(clock)}</small>
            )}
          </div>
        ) : (
          clock && (
            <p className="hint" data-testid="card-clock">
              {clash.hold === 'others'
                ? `Waiting for the others to read the cards · ${secondsLeft(clock)}`
                : `The game goes on in ${secondsLeft(clock)}`}
            </p>
          )
        )}
      </section>
    </>
  );
}

function SupportPlayed({
  score,
  down,
  name,
  picture,
}: {
  score: CardScore | undefined;
  down: boolean;
  name: string | null;
  picture: string | undefined;
}) {
  if (!down)
    return (
      <div className="played-support face-down" aria-label="A support, face down">
        <span>?</span>
      </div>
    );
  if (!score?.cardId)
    return (
      <div className="played-support none" data-testid="played-support">
        <strong>No support</strong>
      </div>
    );
  return (
    <article className="battle-card played-support" data-testid="played-support">
      <div className="card-portrait small">
        {picture ? <img src={picture} alt={name ?? ''} /> : <span>{name?.slice(0, 1)}</span>}
      </div>
      <strong>{name}</strong>
      <small>
        {score.role}
        {score.fit ? ' · fit' : ''}
        {score.counter ? ' · counter' : ''} · +{Math.round(score.support)}
      </small>
    </article>
  );
}

/** Each fighter's share of the chances, as the telling stands, and the needle when it rolls. */
function ChanceBar({
  snapshot,
  clash,
  order,
}: {
  snapshot: SessionSnapshot;
  clash: CardClashView;
  order: number[];
}) {
  const story = clash.story;
  const moment = storyMoment(story);
  const needle = useNeedle(story);
  const factions = snapshot.ctx.board.def.factions;
  return (
    <div className="chance-bar" data-testid="chance-bar">
      {order.map((id) => {
        const fighter = clash.participants.find((p) => p.pieceId === id);
        const chance =
          chanceNow(story, id) ?? clash.scores?.find((s) => s.pieceId === id)?.probability ?? 0;
        const color = factions.find((f) => f.id === fighter?.faction)?.color ?? '#777777';
        const won = moment.over && clash.scores?.find((s) => s.pieceId === id)?.won;
        return (
          <span
            key={id}
            className={`chance${won ? ' won' : ''}`}
            data-testid="chance"
            style={{ width: `${chance * 100}%`, background: color, color: inkOn(color) }}
          >
            {chance >= 0.12 && `${fighter?.name ?? ''} ${Math.round(chance * 100)}%`}
          </span>
        );
      })}
      {needle !== null && (
        <span
          className="chance-needle"
          data-testid="chance-needle"
          style={{ left: `${needle * 100}%` }}
        />
      )}
    </div>
  );
}

/**
 * Where the needle stands along the bar: nowhere before the roll; running back and forth and
 * slowing while it rolls; at the draw once the clash is decided.
 */
function useNeedle(story: CardStory | undefined): number | null {
  const moment = storyMoment(story);
  const [position, setPosition] = useState<number | null>(null);
  const draw = story?.draw ?? null;
  useEffect(() => {
    if (draw === null || (!moment.rolling && !moment.over)) {
      setPosition(null);
      return;
    }
    if (moment.over) {
      setPosition(draw);
      return;
    }
    const start = performance.now();
    let frame = requestAnimationFrame(function run(now) {
      const u = Math.min(1, (now - start) / STORY_PACE.roll);
      setPosition(sweep(u, draw));
      if (u < 1) frame = requestAnimationFrame(run);
    });
    return () => cancelAnimationFrame(frame);
  }, [draw, moment.rolling, moment.over]);
  return position;
}

/** Dark letters on a light side's colour, light ones on a dark side's. */
function inkOn(hex: string): string {
  const v = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  const [r, g, b] = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#1c140c' : '#fff8dc';
}

function secondsLeft(clock: ClockView): string {
  return `${clock.left} s${clock.held ? ' (stopped)' : ''}`;
}
